import axios from 'axios';
import { CiImageOn } from "react-icons/ci";
import { MdDeleteOutline, MdCancel } from "react-icons/md";
import { FaRegSave } from 'react-icons/fa';
import { IoIosCloudDone } from "react-icons/io";
import { AiOutlineFileDone } from 'react-icons/ai';
import { useState, useEffect } from 'react';

const SectionA = ({
  templates,
  files,
  previewPhotos,
  expandedPreviewPhotos,
  periodSelections,
  expiryDates,
  templateErrors,
  userScore,
  totalEmployeeScore,
  selectedSheFileId,
  selectedSheFileName,
  selectedSheFileScore,
  selectedCompany,
  selectedCompanyName,
  selectedDepartment,
  selectedDepartmentName,
  setUserScore,
  setUserTemplatePercentage,
  setTotalCalculatedScore,
  setErrorMessage,
  setShowToaster,
  clearTemplateError,
  setTemplateErrors,
  handleFileChange,
  handleDateChange,
  handleRemoveFile,
  togglePreviewExpanded,
  openFullScreenImage,
  openPdfInNewTab,
  onSave,
  onRefreshTemplates,
}) => {
  const [relatedDocuments, setRelatedDocuments] = useState({});
  const [selectedDocumentData, setSelectedDocumentData] = useState({});

  const fetchRelatedDocuments = async (complianceTemplateSubmissionId) => {
  try {
    
    const response = await axios.get(
      `/api/compliance-tracker/consultant-fill-in/related-template-responses/${complianceTemplateSubmissionId}`
    );
    
    const relatedDocs = response.data;
    
    setRelatedDocuments(prev => ({
      ...prev,
      [complianceTemplateSubmissionId]: relatedDocs
    }));

    // Auto-select logic based on number of documents
    if (relatedDocs.length >= 1) {
      const latestDoc = relatedDocs[0]; // First item is the most recent
      const latestIsApproved = latestDoc.qa_status === 'Approved';
      
      if (relatedDocs.length === 1) {
        // If only one document and it's not approved, auto-select it
        if (!latestIsApproved) {
          setSelectedDocumentData(prev => ({
            ...prev,
            [complianceTemplateSubmissionId]: latestDoc
          }));
        }
      } else {
        // If multiple documents, auto-select the most recent one
        setSelectedDocumentData(prev => ({
          ...prev,
          [complianceTemplateSubmissionId]: latestDoc
        }));
      }
    }
    
  } catch (error) {
    console.error('Error fetching related template responses:', error);
  }
};

  const handleDocumentSelect = (complianceTemplateSubmissionId, documentId) => {
  if (documentId === "") {
    // Find the original template document to preserve its properties
    const originalTemplate = templates.find(
      template => template.compliance_template_submission_id === complianceTemplateSubmissionId
    );
    
    setSelectedDocumentData(prev => ({
      ...prev,
      [complianceTemplateSubmissionId]: {
        template_submission_status: 'Not Started',
        template_user_score: 0,
        template_user_percentage: 0,
        file_expiry_date: null,
        file_period: null,
        // Preserve original template properties
        template_name: originalTemplate?.template_name,
        template_reference: originalTemplate?.template_reference,
        template_score: originalTemplate?.template_score,
        template_frequency: originalTemplate?.template_frequency,
        compliance_template_submission_id: complianceTemplateSubmissionId,
        qa_status: 'Not Started',
        qa_comment: 'No comment'
      }
    }));
    handleRemoveFile(complianceTemplateSubmissionId);
    return;
  }

  const selectedDoc = relatedDocuments[complianceTemplateSubmissionId]?.find(
    doc => doc.template_response_document_id === documentId
  );
  if (selectedDoc) {
    setSelectedDocumentData(prev => ({
      ...prev,
      [complianceTemplateSubmissionId]: selectedDoc
    }));
  }
};

  // Initialize selectedDocumentData to ensure "-- New Submission --" is default
  useEffect(() => {

    const initialSelectedData = {};

    templates.forEach(document => {
      if (document.compliance_template_submission_id) {
        initialSelectedData[document.compliance_template_submission_id] = {
          template_submission_status: 'Not Started',
          template_user_score: 0,
          template_user_percentage: 0,
          file_expiry_date: null,
          file_period: null,
          template_name: document.template_name,
          template_reference: document.template_reference,
          template_score: document.template_score,
          template_frequency: document.template_frequency,
          compliance_template_submission_id: document.compliance_template_submission_id,
          qa_status: document.qa_status || 'Not Started',
          qa_comment: document.qa_comment || 'No comment'
        };
      }
    });
    setSelectedDocumentData(initialSelectedData);

    // Fetch related documents
    templates.forEach(document => {
      if (document.compliance_template_submission_id) {
        fetchRelatedDocuments(document.compliance_template_submission_id);
      }
    });
    
  }, [templates]);

  const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  // If it's already in YYYY-MM-DD format, return as is
  if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return dateString;
  }
  // If it's a Date object or ISO string, extract just the date part
  const date = new Date(dateString);
  return date.toISOString().split('T')[0];
};

  const formatDateForDatabase = (date) => {
    if (!date) return null;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  };

  const handleTemplateFileSubmit = async (document) => {
    const file = files[document.template_id];
    clearTemplateError(document.template_id);

    if (!periodSelections[document.template_id]) {
      setTemplateErrors(prev => ({
        ...prev,
        [document.template_id]: 'Please select a date before saving.'
      }));
      setShowToaster(prev => ({ ...prev, periodError: true }));
      return;
    }

    if (!file) {
      setTemplateErrors(prev => ({
        ...prev,
        [document.template_id]: 'Error - Please select a file.'
      }));
      return;
    }

    try {
      const newUserScore = document.template_score;
      const newPercentage = 100;
      const updatedUserScores = { ...userScore, [document.template_id]: newUserScore };
      const newTotalScore = Object.values(updatedUserScores).reduce((sum, score) => sum + parseFloat(score), 0) + totalEmployeeScore;

      const formData = new FormData();

      //
      formData.append('sheFileId', selectedSheFileId);
      formData.append('sheFileName', selectedSheFileName);
      formData.append('sheFileScore', selectedSheFileScore);

      // 
      formData.append('templateId', document.template_id);
      formData.append('templateName', document.template_name);
      formData.append('templateReference', document.template_reference);
      formData.append('templateScore', document.template_score);
      formData.append('templateFrequency', document.template_frequency);
      formData.append('submissionType', document.submission_type);

      //
      formData.append('fileExpiryDate', formatDateForDatabase(expiryDates[document.template_id]));
      formData.append('userScore', newUserScore);
      formData.append('userPercentage', newPercentage);

      const user = JSON.parse(sessionStorage.getItem('user'));
      
      formData.append('filledInByUserId', user.user_id);
      formData.append('filledInByUserType', user.userType);
      formData.append('filledInByUserCompany', user.companyId);
      formData.append('filledInByUserDepartment', user.department_id);
      formData.append('filledInByUserName', user.userName);
      formData.append('filledInByUserSurname', user.userSurname);
      formData.append('selectedCompanyId', selectedCompany);
      formData.append('selectedCompanyName', selectedCompanyName);
      formData.append('selectedDepartmentId', selectedDepartment);
      formData.append('selectedDepartmentName', selectedDepartmentName);
      formData.append('file', file, file.name);

      await axios.post(
        '/compliance/consultant-fill-in/save-template-file', 
        formData, 
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setUserScore(updatedUserScores);
      setUserTemplatePercentage(prev => ({
        ...prev,
        [document.template_id]: newPercentage
      }));
      setTotalCalculatedScore(newTotalScore);
      setErrorMessage('');
      setShowToaster(prev => ({ ...prev, fileSaved: true, templateSubmitted: true }));

      if (onSave) await onSave();
      if (onRefreshTemplates) await onRefreshTemplates();
      clearTemplateError(document.template_id);
    } catch (error) {
      console.error('Template file submission error:', error.response ? error.response.data : error);
      setTemplateErrors(prev => ({
        ...prev,
        [document.template_id]: 'Error submitting file. Please try again.'
      }));
    }
  };

  return (
    <div>
      <div className='complaince-tracker-employees-medical-upload-main-text-details-container'>
        <h3>Section A - <span className="secondary-color-paragraph">Templates</span></h3>
      </div>

      <div className='compliance-tracker-consultant-fill-in-table-main-container'>
        <div className="compliance-tracker-consultant-fill-in-table-container">
          <table className='compliance-tracker-consultant-fill-in-table'>
            <colgroup>
              <col style={{ width: "150px" }} />
              <col style={{ width: "50px" }} />
              <col style={{ width: "50px" }} />
              <col style={{ width: "50px" }} />
              <col style={{ width: "50px" }} />
              <col style={{ width: "100px" }} />
              <col style={{ width: "250px" }} />
              <col style={{ width: "150px" }} />
              <col style={{ width: "150px" }} />
              <col style={{ width: "150px" }} />
              <col style={{ width: "100px" }} />
              <col style={{ width: "100px" }} />
              <col style={{ width: "100px" }} />
              <col style={{ width: "150px" }} />
              <col style={{ width: "200px" }} />
            </colgroup>
            <thead>
              <tr>
                <th>Template Response Document id</th>
                <th>Compliance Sub id</th>
                <th>Validity</th>
                <th>Status</th>
                <th>Save</th>
                <th>Submit</th>
                <th>Template Name</th>
                <th>Template Reference</th>
                <th>File Frequency</th>
                <th>File Expiry Date</th>
                <th>User Score</th>
                <th>Template Score</th>
                <th>Percentage</th>
                <th>QA Status</th>
                <th>QA Comment</th>
              </tr>
            </thead>
            <tbody>
              {templates.length === 0 ? (
                <tr>
                  <td colSpan="14" className="no-data-message">
                    <i>There are no documents for this SHE file. For more information please contact support</i>
                  </td>
                </tr>
              ) : (
                templates.filter(document => document.submission_type !== 'Manual').map((document, index) => {
                  const selectedDoc = selectedDocumentData[document.compliance_template_submission_id] || document;
                  return (
                    <tr key={index}>
                      <td>
                        <select 
                          className="template-response-dropdown"
                          value={selectedDoc.template_response_document_id || ""}
                          onChange={(e) => handleDocumentSelect(document.compliance_template_submission_id, e.target.value)}
                        >
                          {/* Only show "New Submission" if latest submission is approved OR there are no existing submissions */}
                          {(() => {
                            const relatedDocs = relatedDocuments[document.compliance_template_submission_id];
                            const hasNoSubmissions = !relatedDocs || relatedDocs.length === 0;
                            const latestSubmission = relatedDocs && relatedDocs.length > 0 ? relatedDocs[0] : null;
                            const latestIsApproved = latestSubmission?.qa_status === 'Approved';
                            
                            return (hasNoSubmissions || latestIsApproved) && (
                              <option value=""> -- New Submission -- </option>
                            );
                          })()}
                          
                          {relatedDocuments[document.compliance_template_submission_id]?.map(doc => (
                            <option 
                              key={doc.template_response_document_id} 
                              value={doc.template_response_document_id}
                            >
                            {index + 1} - {doc.template_response_document_id} - {doc.date_submitted.slice(0, 10)}
                            </option>
                          ))}
                        </select>
                      </td>

                      <td>{selectedDoc.compliance_template_submission_id}</td>

                      {/* Column - File Validity */}
                      <td>

                        {selectedDoc.file_validity === 'Valid' ? (
                          <div className="saved-status">
                            <IoIosCloudDone /> {selectedDoc.file_validity}
                          </div>
                        ) : selectedDoc.file_validity === 'Expired' ? (
                          <div className="cancelled-status">
                            <MdCancel /> {selectedDoc.file_validity || 'Not Started'}
                          </div>
                        )  : (
                          <div className="not-saved-status">
                            <MdCancel /> {selectedDoc.file_validity || 'Not Started'}
                          </div>
                        )}

                      </td>

                      <td>
                        {selectedDoc.template_submission_status === 'Saved' ? (
                          <div className="saved-status">
                            <IoIosCloudDone /> {selectedDoc.template_submission_status}
                          </div>
                        ) : selectedDoc.template_submission_status === 'Not Started' ? (
                          <div className="not-saved-status">
                            <MdCancel /> {selectedDoc.template_submission_status}
                          </div>
                        ) : selectedDoc.template_submission_status === 'Completed' ? (
                          <div className="completed-status">
                            <AiOutlineFileDone /> {selectedDoc.template_submission_status}
                          </div>
                        ) : (
                          <div className="not-saved-status">
                            <MdCancel /> {selectedDoc.template_submission_status || 'Not Started'}
                          </div>
                        )}
                      </td>

                      <td>
                        {selectedDoc.qa_status === 'Approved' || selectedDoc.template_submission_status === 'Completed' ? (
                          <i>N/A</i>
                        ) : (
                          <div>
                            <FaRegSave 
                              onClick={() => handleTemplateFileSubmit(document)}
                              className='template-save-icon' 
                            />
                          </div>
                        )}
                      </td>

                      <td>
                        {selectedDoc.qa_status === 'Approved' || selectedDoc.template_submission_status === 'Completed' ? (
                          <i>N/A</i>
                        ) : (
                          <div className="file-upload-container">
                            <input 
                              type="file" 
                              id={`file-upload-${index}`}
                              className="submit-button hidden-file-input"
                              onChange={(e) => handleFileChange(document.template_id, e)}
                              accept=".png,.jpg,.jpeg,.pdf"
                            />
                            {previewPhotos[document.template_id] && (
                              <div className='checklist-question-toaster-image-delete-button-container'>
                                <CiImageOn 
                                  className='checklist-question-toaster-image-icon' 
                                  onClick={() => {
                                    if (previewPhotos[document.template_id] === 'PDF_FILE') {
                                      openPdfInNewTab(files[document.template_id]);
                                    } else {
                                      openFullScreenImage(previewPhotos[document.template_id]);
                                    }
                                  }} 
                                />
                                <button 
                                  type='button' 
                                  className='delete-image-button' 
                                  onClick={() => handleRemoveFile(document.template_id)}
                                > 
                                  <MdDeleteOutline /> 
                                </button>
                              </div>
                            )}
                            {expandedPreviewPhotos[document.template_id] && previewPhotos[document.template_id] && (
                              <img 
                                onClick={() => togglePreviewExpanded(document.template_id)} 
                                src={previewPhotos[document.template_id]} 
                                alt="Preview" 
                                style={{ maxWidth: '200px', maxHeight: '200px' }} 
                              />
                            )}
                          </div>
                        )}
                        {templateErrors[document.template_id] && (
                          <div className="error-text-main-container">
                            {templateErrors[document.template_id]}
                          </div>
                        )}
                      </td>
                      <td>{selectedDoc.template_name}</td>
                      <td>{selectedDoc.template_reference}</td>

                      <td>{selectedDoc.template_frequency}</td>
                      
                      <td>
                        {selectedDoc.qa_status === 'Approved' || selectedDoc.template_submission_status === 'Completed' ? (
                          <span>{formatDateForInput(selectedDoc.file_expiry_date) || 'No expiry date set'}</span>
                        ) : (
                          <input
                            type='date' 
                            value={expiryDates[document.template_id] ? 
                              expiryDates[document.template_id].toISOString().split('T')[0] : 
                              formatDateForInput(selectedDoc.file_expiry_date) || ''
                            }
                            onChange={(e) => {
                              handleDateChange(document.template_id, { target: { value: e.target.value } });
                            }}
                            required={true}
                          />
                        )}
                      </td>
                      <td>{selectedDoc.template_user_score || '0'}</td>
                      <td>{selectedDoc.template_score}</td>
                      <td>{selectedDoc.template_user_percentage || 0} %</td>
                      <td>
                        {selectedDoc.qa_status === 'Approved' ? (
                          <div className="saved-status">
                            <IoIosCloudDone /> {selectedDoc.qa_status}
                          </div>
                        ) : selectedDoc.qa_status === 'Not Started' ? (
                          <div className="not-saved-status">
                            <MdCancel /> {selectedDoc.qa_status}
                          </div>
                        ) : selectedDoc.qa_status === 'Completed' ? (
                          <div className="completed-status">
                            <AiOutlineFileDone /> {selectedDoc.qa_status}
                          </div>
                        ) : (
                          <div className="not-saved-status">
                            <MdCancel /> {selectedDoc.qa_status || 'Not Started'}
                          </div>
                        )}
                      </td>
                      <td>{selectedDoc.qa_comment || 'No comment'}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SectionA;